import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth } from "../../context/AuthContext";
import "./style.scss";

export default function Login() {
  const googleEnabled = Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID);
  const navigate = useNavigate();
  const { login, loginWithGoogle, token } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login({
        email: form.email.trim().toLowerCase(),
        password: form.password
      });
      navigate("/dashboard");
    } catch (err) {
      setError(err?.response?.data?.message || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogleSuccess = async (credentialResponse) => {
    const credential = credentialResponse?.credential;
    if (!credential) {
      setError("Google login failed");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await loginWithGoogle(credential);
      navigate("/dashboard");
    } catch (err) {
      setError(err?.response?.data?.message || "Google login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="auth-page login-page w-full min-w-0">
      <div className="auth-card w-full max-w-md min-w-0 sm:max-w-lg">
        <Link to="/" className="back-to-home">
          ← Back to landing page
        </Link>
        <div className="auth-switch">
          <Link to="/login" className="auth-switch-btn active">
            Login
          </Link>
          <Link to="/register" className="auth-switch-btn">
            Sign Up
          </Link>
        </div>
        <h2 className="!text-2xl !leading-tight sm:!text-3xl">Welcome back</h2>
        <p className="auth-subtitle !text-sm sm:!text-base">Sign in to continue your health journey.</p>
        <form onSubmit={onSubmit} className="auth-form">
          <label htmlFor="login-email">Email</label>
          <input
            id="login-email"
            placeholder="you@example.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
          <label htmlFor="login-password">Password</label>
          <input
            id="login-password"
            placeholder="Enter password"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
          />
          <button
            type="submit"
            disabled={submitting}
            className="mt-2 min-h-11 w-full rounded-[10px] px-4 py-3 text-base font-bold transition-opacity disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto sm:min-w-[8rem]"
          >
            {submitting ? "Signing in..." : "Login"}
          </button>
        </form>
        {googleEnabled && (
          <>
            <div className="auth-divider">
              <span>or</span>
            </div>
            <div className="google-auth">
              <GoogleLogin onSuccess={onGoogleSuccess} onError={() => setError("Google login failed")} />
            </div>
          </>
        )}
        {error && <p className="error">{error}</p>}
        <p className="auth-footer">
          New here? <Link to="/register">Create an account</Link>
        </p>
      </div>
    </section>
  );
}
